import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Card, Form, Layout, Menu, Row, Space, Typography } from "antd";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { SeasonForm } from "./Form";

const collectionBrandsQuery = gql`
  query($pagination: ConnectionPagination, $collectionBrandSorting: CollectionBrandSorting, $searchQuery: String) {
    collectionBrands(pagination: $pagination, sorting: $collectionBrandSorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const createCollectionSeasonMutation = gql`
  mutation($input: CreateCollectionSeasonInput!) {
    createCollectionSeason(input: $input) {
      season {
        id
      }
    }
  }
`;

interface FieldData {
  name: string;
  errors: string[];
}

export const New: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const navigate = useNavigate();

  const { error, loading: queryLoading, data } = useQuery(collectionBrandsQuery, {
    fetchPolicy: "cache-and-network",
  });

  const [execMutation, { loading }] = useMutation(createCollectionSeasonMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      const fieldsErrors: FieldData[] = [];
      const otherErrors: string[] = [];
      graphQLErrors?.forEach((graphQLError) => {
        if (graphQLError.name) {
          fieldsErrors.push({ name: graphQLError.name, errors: (graphQLError as any).messages });
        } else {
          otherErrors.push(graphQLError.message);
        }
      });
      form.setFields(fieldsErrors);
      setOtherErrors(otherErrors);
    },
    onCompleted: (data) => {
      navigate("..");
    },
  });

  const onFinish = (input: any) => {
    execMutation({ variables: { input } });
  };

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="..">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border">
              <Typography.Title level={4} style={{ lineHeight: "auto" }}>
                New Season
              </Typography.Title>
            </Menu.Item>
          </Menu>
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border no-margin">
              <NavLink to="..">
                <Button>Cancel</Button>
              </NavLink>
            </Menu.Item>
            <Menu.Item className="no-border">
              <Button
                type="primary"
                loading={loading}
                onClick={() => {
                  form.submit();
                }}
              >
                Save
              </Button>
            </Menu.Item>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <Layout.Content className="content">
          <Card>
            {otherErrors.length > 0 && (
              <Space direction="vertical" style={{ marginBottom: "10px" }}>
                {otherErrors.map((otherError, index) => (
                  <Alert key={index} type="error" message="Error" description={otherError} />
                ))}
              </Space>
            )}
            <SeasonForm form={form} onFinish={onFinish} data={data} />
          </Card>
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
